/**
 * Builds the payload for PDF generation and calculator data
 * @param {Object} params - Parameters object
 * @param {Function} params.getVal - Function to get form values (e.g., from react-hook-form)
 * @param {Object} params.user - User object with id, first_name, last_name, companyid
 * @param {string} params.main_company_name - Main company name
 * @param {string} params.calculatorname - Calculator name (default: 'Seller Net Sheet')
 * @returns {Object} Payload object for PDF generation
 */
// Sums of several floating-point fields (e.g. allCostsTotal, estimatedSellerNet)
// pick up binary floating-point noise like 40054.770000000004 — round each
// numeric field to 2 decimals right before it goes into the PDF payload,
// without altering the underlying calculation state itself.
const round2 = (n) => {
  const num = typeof n === "number" ? n : parseFloat(n);
  return Number.isFinite(num) ? Number(num.toFixed(2)) : n;
};

const roundTotalClosingMeta = (meta) => {
  if (!meta || typeof meta !== "object") return meta;
  return Object.fromEntries(
    Object.entries(meta).map(([key, value]) => [key, round2(value)])
  );
};

const countMetaRowsWithPositiveValue = (meta) => {
  if (!Array.isArray(meta)) return 0;
  return meta.filter((row) => {
    const raw = row?.value;
    if (raw === undefined || raw === null || raw === '') return false;
    const n = parseFloat(String(raw).replace(/,/g, ''));
    return !Number.isNaN(n) && n > 0;
  }).length;
};

// Property identification fields from the ttb (title/tax/building) lookup —
// APN, owner of record, property type, and other assessor-provided details.
const buildPropertyDetails = (ttbData = {}) => ({
  apn: ttbData?.apn || '',
  ownerName: ttbData?.ownername || '',
  propertyType: ttbData?.type || '',
  assessedValue: ttbData?.assessedValue || '',
  taxStatus: ttbData?.taxStatus || '',
  taxYear: ttbData?.taxYear || '',
  bed: ttbData?.bed || '',
  bath: ttbData?.bath || '',
  sqft: ttbData?.sqft || '',
  built: ttbData?.built || '',
});

export const buildSellerNetPayload = ({
  getVal,
  user,
  calculatorname = 'Seller Net Sheet',
  payload = {},
  mrsid = '',
  optionType = 'save',
  ttbData = {},
}) => {
  const closingCostMeta = getVal('closingCostMeta', '');
  const sellerClosingCostMeta = getVal('sellerClosingCostMeta', '');
  const sellerOtherCostMeta = getVal('sellerOtherCostMeta', '');
  const address = getVal('addressMeta.address', '') || getVal('addressMeta.city', '') + ', ' + getVal('addressMeta.state', '') + ' ' + getVal('addressMeta.zip', '');

  return {
    preparedby: ucWords(getVal('preparedBy', '')) || ucWords(`${user.first_name}  ${user.last_name}`),
    preparedfor: ucWords(getVal('preparedFor', '')) || 'New Client',
    salesPrice: parseFloat(getVal('salesPrice', '').replace(/,/g, '')),
    addressMeta: getVal('addressMeta', ''),
    loanInfoMeta: getVal('loanInfoMeta', ''),
    titleSettlementMeta: getVal('titleSettlementMeta', ''),
    closingCostMeta,
    sellerClosingCostMeta,
    sellerOtherCostMeta,
    compensationMeta: getVal('compensationMeta', ''),
    otherCostMeta: getVal('otherCostMeta', ''),
    totalClosingMeta: roundTotalClosingMeta(getVal('totalClosingMeta', '')),
    calculatorname,
    templateSellerOtherCosts: getVal('templateSellerOtherCosts', {}),
    address,
    // Street View / Aerial View are no longer built here — the backend now
    // fetches them server-side with GOOGLE_MAPS_SERVER_KEY and embeds them
    // as base64 (see enrichSellerPropertyImages in pdf.controller.js).
    // Puppeteer's generatePdf blocks remote image requests, so a plain
    // Google Static API URL built here would never have actually rendered.
    propertyDetails: buildPropertyDetails(ttbData),
    mrsid,
    optionType,
    ...payload,
    metaPositiveCount: countMetaRowsWithPositiveValue(closingCostMeta) + countMetaRowsWithPositiveValue(sellerClosingCostMeta) + countMetaRowsWithPositiveValue(sellerOtherCostMeta),
  };
};

const ucWords = (str = "") => str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

export const buildBuyerPayload = ({ buyerformData, user, payload = {} }) => {

  const buyerClosingCostsMeta = buyerformData.buyerClosingCostsMeta;
  const closingCostsMeta = buyerformData.closingCostsMeta;

  return {
    preparedBy: ucWords(buyerformData.preparedBy) || ucWords(`${user.first_name}  ${user.last_name}`),
    preparedFor: ucWords(buyerformData.preparedFor) || 'New Client',
    salesPrice: parseFloat(buyerformData.salesPrice.replace(/,/g, '')),
    addressMeta: buyerformData.addressMeta,
    loanInfoMeta: buyerformData.loanInfoMeta,
    titleSettlementMeta: buyerformData.titleSettlementMeta,
    buyerClosingCostsMeta,
    closingCostsMeta,
    compensationMeta: buyerformData.compensationMeta,
    prepaidsMeta: buyerformData.prepaidsMeta,
    paymentMeta: buyerformData.paymentMeta,
    totalClosingMeta: roundTotalClosingMeta(buyerformData.totalClosingMeta),
    metaPositiveCount: countMetaRowsWithPositiveValue(buyerClosingCostsMeta) + countMetaRowsWithPositiveValue(closingCostsMeta),
    address: buyerformData?.addressMeta?.address || ucWords(buyerformData?.addressMeta?.city) + ', ' + ucWords(buyerformData?.addressMeta?.state) + ' ' + buyerformData?.addressMeta?.zip,
    ...payload
  }
};

// PropNine's own computed-tax sub-objects — same binary floating-point
// summation noise risk as totalClosingMeta elsewhere, rounded the same way.
const roundTaxScenario = (scenario = {}) => ({
  adjustedCash: round2(scenario?.adjustedCash),
  diffSaleAndPurchase: round2(scenario?.diffSaleAndPurchase),
  taxableValue: round2(scenario?.taxableValue),
  taxWithProp19: round2(scenario?.taxWithProp19),
  taxWithoutProp19: round2(scenario?.taxWithoutProp19),
});

export const propninePayload = ({ buyerformData, user, payload = {} }) => {
  return {
    preparedBy: buyerformData.preparedBy || `${user.first_name}  ${user.last_name}`,
    preparedFor: buyerformData.preparedFor || 'New Client',
    salesPrice: round2(parseFloat(buyerformData.salesPrice.replace(/,/g, ''))),
    purchasePrice: round2(parseFloat(buyerformData.purchasePrice.replace(/,/g, ''))),
    assessedValueAmount: round2(parseFloat(buyerformData.loanInfoMeta.assessedValueAmount.replace(/,/g, ''))),
    caTaxRate: round2(parseFloat(buyerformData.loanInfoMeta.caTaxRate.replace(/,/g, ''))),
    address: buyerformData.addressMeta.address,
    saleBetween365: roundTaxScenario(buyerformData?.estimatedTaxes?.saleBetween365),
    inflationFactor: buyerformData?.estimatedTaxes?.saleBetween730?.inflationFactor,
    saleBetween730: roundTaxScenario(buyerformData?.estimatedTaxes?.saleBetween730),
    ...payload
  }

};
